import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { User, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Store } from '@/types';

interface AuthContextType {
  user: User | null;
  store: Store | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, storeName: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<{ error: AuthError | null }>;
  refreshStore: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Check if the current URL hash contains OAuth tokens (callback in progress) */
function hasOAuthHashTokens(): boolean {
  const hash = window.location.hash;
  return hash.includes('access_token=') || hash.includes('error=') || hash.includes('error_description=');
}

/** Check if an error indicates a corrupted/expired JWT */
function isAuthError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const msg = (error as any)?.message ?? '';
  const code = (error as any)?.code ?? '';
  return (
    msg.includes('JWT') ||
    msg.includes('jwt') ||
    msg.includes('token') ||
    msg.includes('invalid claim') ||
    code === 'PGRST301' ||
    code === '401'
  );
}

/** Check if we're on the custom domain */
function isCustomDomain(): boolean {
  return window.location.hostname.includes('vesteia.com.br');
}

/** Deep clean all Supabase tokens from localStorage */
function clearAllSupabaseTokens() {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('sb-')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [store, setStore] = useState<Store | null>(null);
  const [loading, setLoading] = useState(true);

  /** Fetch store by user_id — robust: uses .limit(1) array instead of maybeSingle */
  const fetchStore = async (userId: string, _userEmail?: string | null) => {
    console.log('[useAuth] fetchStore called for userId:', userId);
    const { data, error } = await supabase
      .from('stores')
      .select('*')
      .eq('user_id', userId)
      .limit(1);

    if (error) {
      console.error('[useAuth] fetchStore error:', error.message, error.code);
      // Rethrow only real errors (network / auth) — caller handles classification
      throw error;
    }

    const store = data && data.length > 0 ? (data[0] as Store) : null;
    if (store) {
      console.log('[useAuth] fetchStore found store:', store.name, store.id);
    } else {
      console.log('[useAuth] fetchStore: no store found for userId:', userId);
    }
    return store;
  };

  const refreshStore = useCallback(async () => {
    if (user) {
      try {
        console.log('[useAuth] refreshStore: starting for user', user.id);
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !sessionData.session) {
          console.warn('[useAuth] refreshStore: session invalid, skipping fetch');
          return;
        }
        const storeData = await fetchStore(user.id, user.email);
        setStore(storeData);
        console.log('[useAuth] refreshStore: result =', storeData ? storeData.name : 'null');
      } catch (e) {
        console.error('[useAuth] refreshStore failed:', e);
      }
    }
  }, [user]);

  useEffect(() => {
    // Safety timeout (5s) — last resort if INITIAL_SESSION never fires
    const safetyTimer = setTimeout(() => {
      setLoading((prev) => {
        if (prev) {
          console.warn('[useAuth] Safety timeout (5s) — forcing loading=false');
          return false;
        }
        return prev;
      });
    }, 5000);

    // Custom domain: force session revalidation before relying on onAuthStateChange
    if (isCustomDomain()) {
      console.log('[useAuth] Custom domain detected, forcing getSession()');
      supabase.auth.getSession().then(({ data, error }) => {
        if (error) {
          console.warn('[useAuth] Custom domain getSession error:', error.message);
          clearAllSupabaseTokens();
        }
      });
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        const currentUser = session?.user ?? null;
        setUser(currentUser);

        if (event === 'INITIAL_SESSION') {
          if (currentUser) {
            try {
              const storeData = await fetchStore(currentUser.id, currentUser.email);
              setStore(storeData);
            } catch (e) {
              console.error('[useAuth] fetchStore failed on INITIAL_SESSION:', e);
              if (isAuthError(e)) {
                console.warn('[useAuth] Auth error detected, signing out');
                try { await supabase.auth.signOut(); } catch {}
                clearAllSupabaseTokens();
                setUser(null);
                setStore(null);
                setLoading(false);
                return;
              }
              setStore(null);
            }
          } else if (hasOAuthHashTokens()) {
            console.log('[useAuth] OAuth callback detected, waiting...');
            return;
          } else {
            setStore(null);
          }
          setLoading(false);
          return;
        }

        // Subsequent events (SIGNED_IN, TOKEN_REFRESHED, etc.)
        if (currentUser) {
          try {
            const storeData = await fetchStore(currentUser.id, currentUser.email);
            setStore(storeData);
          } catch (e) {
            console.error('[useAuth] fetchStore failed on', event, e);
            setStore(null);
          }
        } else {
          setStore(null);
        }
        setLoading(false);
      }
    );

    return () => {
      subscription.unsubscribe();
      clearTimeout(safetyTimer);
    };
  }, []);

  // Realtime subscription for store updates
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`store-auth-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'stores',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            const newStore = payload.new as Store;
            setStore(newStore);
            console.log(`Realtime: store ${payload.eventType.toLowerCase()}d`, newStore.name);
          } else if (payload.eventType === 'DELETE') {
            setStore(null);
            console.log('Realtime: store deleted');
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string, storeName: string) => {
    const { error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          store_name: storeName.trim(),
        },
      },
    });
    return { error };
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.warn('[useAuth] signOut failed, clearing localStorage:', e);
    }
    // Deep clean: remove ALL supabase tokens
    clearAllSupabaseTokens();
    setUser(null);
    setStore(null);
  };

  const signInWithGoogle = async () => {
    if (isCustomDomain()) {
      // Custom domain: use skipBrowserRedirect to get the OAuth URL directly
      // and redirect manually, since Lovable's auth-bridge doesn't exist here
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
          skipBrowserRedirect: true,
          queryParams: {
            prompt: 'select_account',
          },
        },
      });
      if (error) return { error };
      if (data?.url) {
        window.location.href = data.url;
      }
      return { error: null };
    }

    // Lovable preview: use default flow (auth-bridge handles redirect)
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
        queryParams: {
          prompt: 'select_account',
        },
      },
    });
    return { error };
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/auth?tab=reset`,
    });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { error };
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      store, 
      loading, 
      signIn, 
      signUp, 
      signOut, 
      signInWithGoogle, 
      refreshStore,
      resetPassword,
      updatePassword,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
